<template>
    <div class="calificacion-proveedor-container">
        <div class="unified-container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="title-section">
                    <v-icon class="title-icon" color="white">mdi-file-pdf-box</v-icon>
                    <div class="title-text">
                        <h1 class="main-title">CALIFICACIONES DE PROVEEDORES</h1>
                        <p class="subtitle">Visualización de calificaciones realizadas por directores de área</p>
                    </div>
                </div>
            </div>

            <!-- Search and Filter Section -->
            <div class="search-section">
                <div class="search-filters">
                    <div class="search-card">
                        <v-text-field
                            v-model="search"
                            append-icon="mdi-magnify"
                            label="Buscar proveedor por NIT, nombre o contacto"
                            solo
                            flat
                            hide-details
                            class="search-field"
                        ></v-text-field>
                    </div>
                    <div class="filter-card">
                        <v-select
                            v-model="filtroEstado"
                            :items="opcionesFiltro"
                            item-text="text"
                            item-value="value"
                            label="Filtrar por estado"
                            solo
                            flat
                            hide-details
                            class="filter-field"
                        ></v-select>
                    </div>
                    <div class="filter-card">
                        <v-select
                            v-model="filtroNivel"
                            :items="opcionesNivel"
                            item-text="text"
                            item-value="value"
                            label="Filtrar por nivel"
                            solo
                            flat
                            hide-details
                            clearable
                            class="filter-field"
                        ></v-select>
                    </div>
                    <div class="filter-card">
                        <v-select
                            v-model="filtroTipo"
                            :items="tiposFormulario"
                            item-text="text"
                            item-value="value"
                            label="Filtrar por tipo de formulario"
                            solo
                            flat
                            hide-details
                            clearable
                            class="filter-field"
                        ></v-select>
                    </div>
                </div>
            </div>

            <!-- Table Section -->
            <div class="table-section">
                <div class="table-card">
                    <v-data-table
                        :headers="headers"
                        :items="proveedoresFiltrados"
                        :search="search"
                        class="modern-table"
                        :items-per-page="10"
                        :loading="loading"
                        loading-text="Cargando proveedores..."
                        :footer-props="{
                            'items-per-page-options': [10, 20, 50],
                            'items-per-page-text': 'Filas por página:',
                        }"
                    >
                        <template v-slot:no-data>
                            <v-alert
                                :value="true"
                                color="#F44336"
                                icon="mdi-alert"
                                outlined
                                type="warning"
                                prominent
                                border="left"
                            >
                                <strong>No</strong> se encontraron proveedores
                            </v-alert>
                        </template>

                        <!-- Columna Nivel con chip de color -->
                        <template v-slot:[`item.nivel`]="{ item }">
                            <v-chip
                                :color="item.nivel === 'critico' ? 'error' : 'success'"
                                outlined
                                small
                            >
                                {{ item.nivel === 'critico' ? 'CRÍTICO' : 'NO CRÍTICO' }}
                            </v-chip>
                        </template>

                        <!-- Columna Estado -->
                        <template v-slot:[`item.estado`]="{ item }">
                            <v-chip
                                :color="item.estado === 'calificado' ? 'success' : 'warning'"
                                outlined
                                class="ma-2"
                            >
                                {{ item.estado === 'calificado' ? 'CALIFICADO' : 'PENDIENTE' }}
                            </v-chip>
                        </template>

                        <!-- Columna Promedio (solo se muestra si está calificado) -->
                        <template v-slot:[`item.promedio`]="{ item }">
                            <div v-if="item.estado === 'calificado' && item.promedio">
                                <v-chip :color="getPromedioColor(item.promedio)" dark>
                                    {{ item.promedio.toFixed(1) }} / 5.0
                                </v-chip>
                            </div>
                            <div v-else>
                                <span class="grey--text">---</span>
                            </div>
                        </template>

                        <!-- Columna Tipo -->
                        <template v-slot:[`item.tipo`]="{ item }">
                            <div v-if="item.estado === 'calificado' && item.tipo">
                                <v-chip outlined small>
                                    {{ getTipoFormularioTexto(item.tipo) }}
                                </v-chip>
                            </div>
                            <div v-else>
                                <span class="grey--text">---</span>
                            </div>
                        </template>

                        <!-- Columna Calificado por -->
                        <template v-slot:[`item.calificado_por`]="{ item }">
                            <div v-if="item.estado === 'calificado' && item.nombreUsuario">
                                <div class="font-weight-bold">{{ item.nombreUsuario || 'N/A' }}</div>
                                <div class="caption text-muted">{{ item.nombreRol || '' }} | {{ item.nombreArea || '' }}</div>
                            </div>
                            <div v-else>
                                <span class="grey--text">Sin calificar</span>
                            </div>
                        </template>

                        <!-- Columna Fecha -->
                        <template v-slot:[`item.fecha_calificacion`]="{ item }">
                            <div v-if="item.estado === 'calificado' && item.fecha_calificacion">
                                {{ formatearFecha(item.fecha_calificacion) }}
                            </div>
                            <div v-else>
                                <span class="grey--text">---</span>
                            </div>
                        </template>

                        <!-- Columna Acciones -->
                        <template v-slot:[`item.acciones`]="{ item }">
                            <div class="action-icons">
                                <v-btn
                                    v-if="item.estado === 'calificado'"
                                    class="pdf-btn"
                                    small
                                    color="error"
                                    @click="verPDF(item)"
                                >
                                    <v-icon left small>mdi-file-pdf-box</v-icon>
                                    Ver PDF
                                </v-btn>
                                <span v-else class="grey--text text-caption">No disponible</span>
                            </div>
                        </template>
                    </v-data-table>
                </div>
            </div>

            <!-- Promedio General -->
            <div class="promedio-general-section" v-if="promedioGeneral > 0">
                <div class="promedio-general-card">
                    <div class="promedio-general-label">
                        <v-icon color="#3b82f6">mdi-chart-line</v-icon>
                        <span>PROMEDIO GENERAL DE TODOS LOS PROVEEDORES CALIFICADOS</span>
                    </div>
                    <div class="promedio-general-valor">
                        <span class="promedio-numero">{{ promedioGeneral.toFixed(1) }}</span>
                        <span class="promedio-max">/ 5.0</span>
                    </div>
                </div>
            </div>

            <v-snackbar v-model="snackbar" :bottom="true" :timeout="timeout" :color="snackbarColor" class="modern-snackbar">
                {{ snackbarText }}
                <v-btn color="white" text @click="snackbar = false">Cerrar</v-btn>
            </v-snackbar>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import env from "../env";

export default {
    name: "CalificacionProveedor",
    data() {
        return {
            search: "",
            filtroEstado: "todos",
            filtroNivel: null,
            filtroTipo: null,
            proveedores: [],
            loading: false,
            snackbar: false,
            snackbarText: "",
            snackbarColor: "success",
            timeout: 6000,
            
            opcionesFiltro: [
                { text: "Todos", value: "todos" },
                { text: "Calificados", value: "calificado" },
                { text: "Pendientes", value: "pendiente" }
            ],
            
            opcionesNivel: [
                { text: "Crítico", value: "critico" },
                { text: "No Crítico", value: "no_critico" }
            ],
            
            tiposFormulario: [
                { text: "SEGURIDAD E INFRAESTRUCTURA", value: "seguridad" },
                { text: "SERVICIOS PROFESIONALES", value: "servicios" },
                { text: "DOTACIÓN - PAPELERÍA", value: "dotacion" }
            ],
            
            headers: [
                { text: "#", value: "index", align: "center", width: "50px" },
                { text: "NIT", value: "nit", align: "left", width: "140px" },
                { text: "Nombre Empresa", value: "nombre_empresa", align: "left", width: "220px" },
                { text: "Contacto", value: "contacto", align: "left", width: "180px" },
                { text: "Teléfono", value: "telefono", align: "center", width: "110px" },
                { text: "Nivel", value: "nivel", align: "center", width: "100px" },
                { text: "Estado", value: "estado", align: "center", width: "120px" },
                { text: "Promedio", value: "promedio", align: "center", width: "100px" },
                { text: "Tipo", value: "tipo", align: "center", width: "120px" },
                { text: "Calificado por", value: "calificado_por", align: "left", width: "200px" },
                { text: "Fecha", value: "fecha_calificacion", align: "center", width: "120px" },
                { text: "Acciones", value: "acciones", align: "center", width: "120px", sortable: false }
            ]
        };
    },
    
    computed: {
        proveedoresFiltrados() {
            let resultado = this.proveedores;
            
            // Filtrar por estado
            if (this.filtroEstado === "calificado") {
                resultado = resultado.filter(p => p.estado === "calificado");
            } else if (this.filtroEstado === "pendiente") {
                resultado = resultado.filter(p => p.estado === "pendiente");
            }
            
            // Filtrar por nivel
            if (this.filtroNivel) {
                resultado = resultado.filter(p => p.nivel === this.filtroNivel);
            }
            
            // Filtrar por tipo de formulario
            if (this.filtroTipo) {
                resultado = resultado.filter(p => p.tipo === this.filtroTipo);
            }
            
            return resultado;
        },
        
        promedioGeneral() {
            const calificados = this.proveedores.filter(p => p.promedio && p.promedio > 0);
            if (calificados.length === 0) return 0;
            const suma = calificados.reduce((a, b) => a + b.promedio, 0);
            return suma / calificados.length;
        }
    },
    
    mounted() {
        this.obtenerProveedores();
    },
    
    methods: {
        obtenerProveedores() {
            this.loading = true;
            axios.get(`${env.endpoint}user/obtenerProveedores`)
                .then(res => {
                    if (res.data.success) {
                        this.proveedores = res.data.proveedores.map((proveedor, index) => {
                            let calificacionData = null;
                            let calificado = false;
                            let promedio = null;
                            let respuestas = [];
                            let tipo = null;
                            let observaciones = "";
                            let fecha_calificacion = null;
                            let nombreUsuario = null;
                            let nombreRol = null;
                            let nombreArea = null;
                            
                            if (proveedor.calificacion) {
                                if (typeof proveedor.calificacion === 'object') {
                                    calificacionData = proveedor.calificacion;
                                } else if (typeof proveedor.calificacion === 'string') {
                                    try {
                                        calificacionData = JSON.parse(proveedor.calificacion);
                                    } catch(e) {
                                        calificacionData = null;
                                    }
                                }
                                
                                if (calificacionData) {
                                    calificado = true;
                                    promedio = calificacionData.promedio || null;
                                    respuestas = calificacionData.respuestas || [];
                                    tipo = calificacionData.tipo || null;
                                    observaciones = calificacionData.observaciones || "";
                                    fecha_calificacion = calificacionData.fecha_calificacion || null;
                                    nombreUsuario = calificacionData.nombreUsuario || null;
                                    nombreRol = calificacionData.nombreRol || null;
                                    nombreArea = calificacionData.nombreArea || null;
                                }
                            }
                            
                            return {
                                ...proveedor,
                                index: index + 1,
                                estado: calificado ? "calificado" : "pendiente",
                                promedio: promedio,
                                respuestas: respuestas,
                                tipo: tipo,
                                observaciones: observaciones,
                                fecha_calificacion: fecha_calificacion,
                                nombreUsuario: nombreUsuario,
                                nombreRol: nombreRol,
                                nombreArea: nombreArea,
                                nivel: proveedor.nivel || "no_critico"
                            };
                        });
                    } else {
                        this.mostrarSnackbar("Error al cargar proveedores", "error");
                    }
                })
                .catch(error => {
                    console.error("Error:", error);
                    this.mostrarSnackbar("Error de conexión al servidor", "error");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        
        verPDF(item) {
            if (!item || !item.idProveedor) {
                this.mostrarSnackbar("No se pudo generar el PDF", "error");
                return;
            }
            
            // Usar el mismo estilo que en todosProcesos.vue para abrir el PDF
            const url = `${env.endpoint}user/generarPDFCalificacion?idProveedor=${item.idProveedor}`;
            window.open(url, '_blank');
        },
        
        getTipoFormularioTexto(tipo) {
            const tipos = {
                "seguridad": "SEGURIDAD E INFRAESTRUCTURA",
                "servicios": "SERVICIOS PROFESIONALES",
                "dotacion": "DOTACIÓN - PAPELERÍA "
            };
            return tipos[tipo] || tipo;
        },
        
        getPromedioColor(promedio) {
            if (promedio >= 4.5) return "success";
            if (promedio >= 3.5) return "info";
            if (promedio >= 2.5) return "warning";
            if (promedio >= 1.5) return "orange";
            return "error";
        },
        
        formatearFecha(fecha) {
            if (!fecha) return "No disponible";
            const date = new Date(fecha);
            return date.toLocaleDateString("es-ES", {
                year: "numeric",
                month: "long",
                day: "numeric"
            });
        },
        
        mostrarSnackbar(texto, tipo = "success") {
            this.snackbarText = texto;
            this.snackbarColor = tipo === "success" ? "#4caf50" : tipo === "error" ? "#f44336" : "#ff9800";
            this.snackbar = true;
        }
    }
};
</script>

<style scoped>
.calificacion-proveedor-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
    padding: 1.5rem;
}

.unified-container {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    background: white;
    border-radius: 0;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.header-section {
    background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
    padding: 2rem;
    color: white;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.title-icon {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.main-title {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
}

.subtitle {
    font-size: 1rem;
    margin: 0;
    opacity: 0.9;
}

.search-section {
    padding: 1.5rem 2rem;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.search-filters {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
}

.search-card {
    flex: 2;
    min-width: 250px;
}

.filter-card {
    flex: 1;
    min-width: 180px;
}

.search-field,
.filter-field {
    background: white !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.table-section {
    padding: 0;
    background: white;
    width: 100%;
    overflow-x: auto;
}

.modern-table {
    background: white !important;
    min-width: 1300px;
}

.modern-table >>> .v-data-table__wrapper {
    overflow-x: scroll !important;
}

.modern-table >>> .v-data-table-header th {
    color: #374151 !important;
    font-weight: 600 !important;
    font-size: 0.75rem !important;
    text-transform: uppercase !important;
    padding: 0.65rem 0.5rem !important;
    background: #f9fafb !important;
    border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table__wrapper table tbody tr td {
    padding: 1rem 0.75rem !important;
    font-size: 0.875rem !important;
}

.action-icons {
    display: flex;
    justify-content: center;
}

.pdf-btn {
    text-transform: none !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
}

.promedio-general-section {
    padding: 1.5rem 2rem;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
}

.promedio-general-card {
    background: white;
    border-radius: 12px;
    padding: 1rem 2rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 2rem;
}

.promedio-general-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
}

.promedio-general-valor {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
}

.promedio-numero {
    font-size: 2rem;
    font-weight: 700;
    color: #3b82f6;
}

.promedio-max {
    font-size: 1rem;
    color: #64748b;
}

.modern-snackbar {
    border-radius: 8px !important;
}

@media (max-width: 768px) {
    .calificacion-proveedor-container {
        padding: 0.75rem;
    }
    
    .header-section {
        padding: 1.25rem;
        text-align: center;
    }
    
    .title-section {
        flex-direction: column;
        text-align: center;
        gap: 0.75rem;
    }
    
    .main-title {
        font-size: 1.3rem;
    }
    
    .search-section {
        padding: 1rem;
    }
    
    .search-filters {
        flex-direction: column;
    }
    
    .search-card,
    .filter-card {
        width: 100%;
        min-width: auto;
    }
    
    .promedio-general-section {
        justify-content: center;
    }
    
    .promedio-general-card {
        flex-direction: column;
        gap: 0.5rem;
        text-align: center;
    }
}
</style>